import React, { useCallback, useRef, useState } from 'react';
import snakeCase from 'lodash/snakeCase';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { type FlagId, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import IdBadge from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/id-badge';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	CSM_CUSTOMER_CONTEXT_FEATURE,
	CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { FeatureToggleTile } from '../../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../../controllers/common/service-project-features-controller/index.tsx';
import { AsyncConfirmDisableModal } from './confirm-disable-modal/async';
import messages from './messages';

type Props = {
	isJiraAdmin: boolean;
	isProductsAndEntitlementsEnabled: boolean;
};

const CustomerContextFeatureTile = ({ isJiraAdmin, isProductsAndEntitlementsEnabled }: Props) => {
	const { formatMessage } = useIntl();
	const { data, saving: savingValue, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();

	const [projectKey = ''] = usePathParam('projectKey');
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	const updateFeatureValue = useCallback(
		(featureTitle: string, feature: string, enabled: boolean) =>
			update({
				feature,
				enabled,
				onSuccess: () => {
					const flagIdKey = `${feature}-service-feature-success`;
					const { current: flagIds } = flagIdRef;
					// toFlagId creates a new string reference. But we want to reuse
					// flag ids so the most relevant flag for a feature is shown
					flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

					const id = flagIds[flagIdKey];
					const onClick = () => {
						dismissFlag(id);
					};
					// Dismiss previous flag with the same id. So user cannot
					// interact with out of update flags
					dismissFlag(id);

					showFlag({
						id,
						type: 'success',
						isAutoDismiss: !enabled,
						title: [messages.successFlagTitle, { feature: snakeCase(feature), value: enabled }],
						description: enabled
							? messages.successEnableDescription
							: messages.successDisableDescription,
						actions: enabled
							? [
									{
										content: messages.successEnableAction,
										onClick: () => {
											dismissFlag(id);
											push(`/jira/servicedesk/projects/${projectKey}/customers`);
										},
									},
								]
							: [
									{
										content: messages.successDisableDismiss,
										onClick,
									},
								],
					});
				},
				onFail: () => {
					showSaveErrorFlag(featureTitle, enabled);
				},
			}),
		[dismissFlag, projectKey, push, showFlag, showSaveErrorFlag, update],
	);

	const inProductHelpArticleId = '6u1dZowoLdD0MSj0H7QWxK';
	const fallbackArticleUrl =
		'https://support.atlassian.com/jira-service-management-cloud/docs/what-is-customer-service-management/';
	const inProductHelpLink = (
		<HelpPanelLink
			appearance="link"
			spacing="none"
			articleId={inProductHelpArticleId}
			fallbackComponent={
				<a target="_blank" href={fallbackArticleUrl}>
					{formatMessage(messages.csmCustomerContextLearnMore)}
				</a>
			}
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({}),
					'helpLink clicked',
					'learnMoreAboutCsmCustomerContext',
					{
						inProductHelpArticleId,
					},
				)
			}
		>
			{formatMessage(messages.csmCustomerContextLearnMore)}
		</HelpPanelLink>
	);

	let tooltipMessage = null;
	if (!isJiraAdmin) {
		tooltipMessage = formatMessage(messages.csmCustomerContextContactAdmin);
	}

	const [isDisableCsmModalOpen, setIsDisableCsmModalOpen] = useState(false);

	const onConfirmCsmDisable = useCallback(() => {
		updateFeatureValue(
			formatMessage(messages.csmCustomerContextTitle),
			CSM_CUSTOMER_CONTEXT_FEATURE,
			false,
		);
		updateFeatureValue(
			formatMessage(messages.productsAndEntitlementsFeatureTitle),
			CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE,
			false,
		);
		setIsDisableCsmModalOpen(false);
	}, [formatMessage, updateFeatureValue]);

	const closeDisableCsmModal = useCallback(() => {
		setIsDisableCsmModalOpen(false);
	}, []);

	const disableCustomerServiceManagement = useCallback(() => {
		setIsDisableCsmModalOpen(true);
	}, []);

	const createCsmOnChangeHandler = useCallback(
		(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });
			if (enabled || (!enabled && !isProductsAndEntitlementsEnabled)) {
				updateFeatureValue(formatMessage(messages.csmCustomerContextTitle), feature, enabled);
			} else {
				disableCustomerServiceManagement();
			}
		},
		[
			disableCustomerServiceManagement,
			formatMessage,
			isProductsAndEntitlementsEnabled,
			updateFeatureValue,
		],
	);

	return (
		<>
			<FeatureToggleTile
				feature={CSM_CUSTOMER_CONTEXT_FEATURE}
				title={formatMessage(messages.csmCustomerContextTitle)}
				illustration={<IdBadge alt="" />}
				value={data?.[CSM_CUSTOMER_CONTEXT_FEATURE]?.status === ENABLED}
				saving={savingValue[CSM_CUSTOMER_CONTEXT_FEATURE] || isDisableCsmModalOpen}
				onChange={createCsmOnChangeHandler}
				disabled={!isJiraAdmin}
				tooltip={tooltipMessage}
			>
				<FormattedMessage
					{...messages.csmCustomerContextDescriptionForIph}
					values={{
						learnMoreLink: inProductHelpLink,
						br: (
							<>
								<br />
								<br />
							</>
						),
					}}
				/>
			</FeatureToggleTile>
			{isDisableCsmModalOpen && (
				<AsyncConfirmDisableModal onClose={closeDisableCsmModal} onConfirm={onConfirmCsmDisable} />
			)}
		</>
	);
};

export default CustomerContextFeatureTile;
